import { composeModules, HolocronLoadModuleDataParams } from 'holocron';
import { loadLanguagePack } from '@americanexpress/one-app-ducks';
import { RAMPED_UP_VARIANT, ALLOW_LIST_VARIANT } from '@americanexpress/customer-dashboard-utils';

import { getLocale, getIsSupportedMarket } from '../selectors';
import {
  injectExternalProductData,
  loadProductData,
  loadProductNavigationLinks,
  loadSubModules,
  loadTooltipPreference,
  loadCredoEligibility,
} from '../utils/loadHelpers';

const loadModuleData = async ({
  store,
  store: { dispatch, getState },
  ownProps: {
    view: propsView,
    productsLocation: propsProductsLocation,
    route: { view: routeView, productsLocation: routeProductsLocation } = {},
    ...ownProps
  },
}: HolocronLoadModuleDataParams<ConsumerNavigationProps>): Promise<unknown[]> => {
  // get locale from state, not a true prop during ssr
  const locale = getLocale(getState());

  const view = propsView || routeView || 'PRODUCT';
  const productsLocation = propsProductsLocation || routeProductsLocation;
  const props = {
    ...ownProps,
    locale,
    view,
    productsLocation,
  };

  // seed local product state with external data already available
  injectExternalProductData(store, props);

  const { isEligible, variantType } = await loadCredoEligibility(store);

  const isConsumerNavEligible = isEligible
    && (variantType === RAMPED_UP_VARIANT || variantType === ALLOW_LIST_VARIANT)
    && getIsSupportedMarket(getState());

  // return with only loading legacy nav in unsupported markets
  if (!isConsumerNavEligible) {
    if (view === 'ACCOUNT') {
      return Promise.resolve([]);
    }

    return dispatch(composeModules([
      { name: 'axp-myca-site-area-nav' },
    ]));
  }

  // await registry and other necessary details
  // as the details are needed to load the product navigation links
  await loadProductData(store);

  // load language pack
  const langPackPromise = dispatch(
    loadLanguagePack('axp-consumer-navigation', { fallbackLocale: 'en-US' })
  );

  // load product navigation links
  const productNavigationLinksPromise = loadProductNavigationLinks(store, props);

  // load sub modules
  const subModulesPromise = loadSubModules(store, props);

  // load tooltip preferences
  const tooltipPreferencesPromise = loadTooltipPreference(store, props);

  return Promise.all([
    productNavigationLinksPromise,
    langPackPromise,
    subModulesPromise,
    tooltipPreferencesPromise,
  ]);
};

export default loadModuleData;
